import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { SolidParticle } from '@babylonjs/core/Particles/solidParticle';
import { SolidParticleSystem } from '@babylonjs/core/Particles/solidParticleSystem';
import { DeepImmutable } from '@babylonjs/core/types';

import type { PatternFeature } from '@kb3d/models/classes/features/pattern-feature';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

import { ensureNormalsExist } from '../utils';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';

type ParticleUpdaterFunction = (result: SolidParticle) => void;
interface IPatternDef {
    sps: SolidParticleSystem;
    size: number;
    resultMesh?: Mesh;
    updater: ParticleUpdaterFunction;
    needsUpdate: boolean; // Whether we need to reposition the particles on this pattern
    needsRebuild: boolean; // Whether we need to rebuild the pattern mesh to create less or more geometry polygons
    needsSubmeshRebuild: boolean; // Whether we need to rebuild the submesh data on the pattern mesh
    shape: Mesh;
}
const identityRotation = Quaternion.Identity();

// This is passed to the worker as additional information needed
export type PatternGeometryWorkerPayload = {
    /** 3n vector array */
    sketchPoints?: DeepImmutable<Float32Array | number[]>;
    otherMeshTransform?: DeepImmutable<Float32Array | number[]>;
    thisMeshTransform: DeepImmutable<Float32Array | number[]>;
};

// This is processed and deserialized in the worker based on the worker payload
export type PatternGeometryDependencyData = {
    sketchPoints?: Vector3[];
    otherMeshTransform?: Matrix;
    thisMeshTransform: Matrix;
};

export class PatternGeometryProcessor<T extends PatternFeature> extends FeatureGeometryProcessor<T> {
    readWorkerPayload(host: WorkerProcessorHost, data: PatternGeometryWorkerPayload) {
        if (!data) {
            return null;
        }
        const sketchPoints = [] as Vector3[];
        if (data.sketchPoints) {
            for (let i = 0; i < data.sketchPoints.length; i += 3) {
                const x = data.sketchPoints[i];
                const y = data.sketchPoints[i + 1];
                const z = data.sketchPoints[i + 2];
                sketchPoints.push(new Vector3(x, y, z));
            }
        }
        const otherMeshTransform =data.otherMeshTransform ? Matrix.FromArray(data.otherMeshTransform) : undefined;
        const thisMeshTransform = Matrix.FromArray(data.thisMeshTransform);
        const joinGeometryDependencyData: PatternGeometryDependencyData = {
            sketchPoints,
            otherMeshTransform,
            thisMeshTransform,
        };
        return joinGeometryDependencyData;
    }

    updateMeshGeometry(
        feature: T,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<PatternGeometryDependencyData>,
        setParticlePositionFunc?: (idx: number, particle: SolidParticle) => void
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);

        const meshUpdated = new Mesh('patternTempGeometry');
        meshUpdated.setEnabled(false);
        const tempVertexData = new SubmeshVertexData();
        tempVertexData.copyFrom(newVertexData);
        tempVertexData.applyToMesh(meshUpdated, false);

        ensureNormalsExist(meshUpdated);

        const sps = new SolidParticleSystem(`${dependencyData.meshDynamicId}_${feature._dynamicId}`, this.scene, {
            enableDepthSort: false,
            enableMultiMaterial: true,
            expandable: true,
        });

        if (feature) {
            if (feature.meshId) {
                const data = dependencyData.data;
                if (!data || !data.sketchPoints) {
                    return newVertexData;
                }

                const positionArray = data.sketchPoints;
                // use transform here

                sps.addShape(meshUpdated, positionArray.length);
                sps.counter = positionArray.length;
                feature.count = positionArray.length;

                const thisTransformInv = data.thisMeshTransform.clone().invert();

                sps.initParticles = () => {
                    for (let p = 0; p < sps.counter; p++) {
                        const particle = sps.particles[p];
                        const newVector = Vector3.Zero();
                        newVector.copyFrom(positionArray[p]);
                        if(data.otherMeshTransform) {
                            Vector3.TransformCoordinatesToRef(newVector, data.otherMeshTransform, newVector);
                        }
                        Vector3.TransformCoordinatesToRef(newVector, thisTransformInv, newVector);
                        
                        particle.position = newVector;
                    }
                };
            } else {
                sps.addShape(meshUpdated, feature.count + 1);
                sps.initParticles = () => {
                    for (let p = 0; p < sps.nbParticles; p++) {
                        const particle = sps.particles[p];
                        if (particle.rotationQuaternion) {
                            particle.rotationQuaternion!.copyFrom(identityRotation);
                        } else {
                            particle.rotationQuaternion = Quaternion.Identity();
                        }
                        particle.position.setAll(0);
                    }
                };
            }
        }

        const updater: ParticleUpdaterFunction = particle => {
            if (feature.enabled) {
                const particleIndex = particle.idx;
                if (particleIndex >= pattern.size) {
                    particle.isVisible = false;
                    return;
                }
                if (!particle.isVisible) {
                    particle.isVisible = true;
                }

                const i = particleIndex % feature.count;
                if (i > 0 && setParticlePositionFunc) {
                    setParticlePositionFunc(i, particle);
                }
            }
        };
        const pattern: IPatternDef = {
            sps,
            size: 1,
            updater,
            needsUpdate: true,
            needsRebuild: true,
            needsSubmeshRebuild: true,
            shape: meshUpdated,
        };
        sps.updateParticle = particle => {
            pattern.updater(particle);
            // Do we need this?
            // particle.scaling.copyFrom(mesh.scaling);
            return particle;
        };
        pattern.needsUpdate = true;

        pattern.size = feature.count;

        if (
            !pattern.needsRebuild &&
            (vertexDataChanges.indices || vertexDataChanges.positions || vertexDataChanges.uvs)
        ) {
            /**
             * If the shape of the mesh changed, then we have to remove and re-add every particle to recompute the geometry.
             * Potential optimization here is to only recompute vertex positions if the vertex count is the same,
             * and only do the following expensive operation if the vertex count (indices?) changes.
             */
            const currentSize = pattern.sps.nbParticles;
            pattern.sps.addShape(pattern.shape, Math.max(pattern.size, pattern.sps.nbParticles));
            pattern.sps.removeParticles(0, currentSize - 1);
            pattern.needsRebuild = true;
        } else if (pattern.sps.nbParticles < pattern.size) {
            // If we don't have enough particles to complete the pattern, expand the SPS as needed
            pattern.sps.addShape(pattern.shape, pattern.size - pattern.sps.nbParticles);
            pattern.needsRebuild = true;
        }

        if (vertexDataChanges.submeshData) {
            pattern.needsSubmeshRebuild = true;
        }

        this.processParticleSystem(pattern, newVertexData); // mutates newVertexData

        return newVertexData;
    }

    processParticleSystem(pattern: IPatternDef, resultVertexData: Partial<SubmeshVertexData>) {
        if (!pattern.needsUpdate) {
            return;
        }

        // Shrink the particle system if there are way too many particles (more than 50% too many)
        // We don't want to shrink unless necessary beca use there's a performance hit to rebuilding the mesh
        if (pattern.sps.nbParticles - pattern.size > pattern.size / 2) {
            pattern.sps.removeParticles(pattern.size, pattern.sps.nbParticles - 1);
            pattern.needsRebuild = true;
        }

        pattern.sps.initParticles();

        if (!pattern.resultMesh || pattern.needsRebuild) {
            pattern.resultMesh = pattern.sps.buildMesh();
            pattern.needsRebuild = false;
            pattern.needsSubmeshRebuild = true;
            pattern.resultMesh.setEnabled(false);
        }

        pattern.sps.computeBoundingBox = true;
        pattern.sps.setParticles();

        const totalIndicesSourceCount = resultVertexData.indices!.length;
        const totalVerticesSourceCount = resultVertexData.positions!.length / 3;

        const newVertexData = VertexData.ExtractFromMesh(pattern.resultMesh);
        resultVertexData.indices = newVertexData.indices;
        resultVertexData.positions = newVertexData.positions;
        if (newVertexData.normals) {
            resultVertexData.normals = newVertexData.normals;
        }
        if (resultVertexData.uvs && newVertexData.uvs) {
            // Don't add UVs if UVs never existed
            resultVertexData.uvs = newVertexData.uvs;
        }

        // If the source mesh has submeshes, then we need to update them
        if (resultVertexData.submeshData) {
            const submeshesToClone = resultVertexData.submeshData.slice(1);

            const totalIndicesResultCount = resultVertexData.indices!.length;
            const totalVerticesResultCount = resultVertexData.positions!.length / 3;
            // resultMeshCsg.dispose();
            const baseMaterialIndex = resultVertexData.submeshData[0].materialIndex;

            resultVertexData.submeshData = [];
            resultVertexData.submeshData.push({
                materialIndex: baseMaterialIndex,
                verticesStart: 0,
                verticesCount: totalVerticesResultCount,
                indexStart: 0,
                indexCount: totalIndicesResultCount,
            });

            const spsPatternSize = totalVerticesResultCount / totalVerticesSourceCount;

            if (submeshesToClone.length > 0) {
                submeshesToClone.forEach(subMesh => {
                    for (let i = 0; i < spsPatternSize; i++) {
                        resultVertexData.submeshData!.push({
                            materialIndex: subMesh.materialIndex,
                            verticesStart: subMesh.verticesStart + i * totalVerticesSourceCount,
                            verticesCount: subMesh.verticesCount,
                            indexStart: subMesh.indexStart + i * totalIndicesSourceCount,
                            indexCount: subMesh.indexCount,
                        });
                    }
                });
            }
        }
    }

    delete3d(feature: T, preserveCache?: boolean): void {
        super.delete3d(feature, preserveCache);
    }

    dispose(): void {
        super.dispose();
    }
}
